import { isMapEditing } from './editlock';
// A draggable rotate handle for directional objects (sectored radars / SAMs).
// When such an entity is selected, an arm reaches from its centre to a grip; the
// operator drags the grip around to re-aim the system. The bearing is committed
// (a PUT) on release. Geographic bearings throughout, so it behaves correctly
// under map rotation and pitch. The grip sits a fixed number of screen pixels
// out, so it stays a comfortable size at every zoom.

import * as maplibregl from 'maplibre-gl';
import { registerHover } from './hover';
import { bearingDegBetween, haversineKm, normalizeBearing } from './radar';

const SRC = 'rotate-handle';
export const GRIP_LAYER = 'rotate-grip';
const ARM_LAYER = 'rotate-arm';
const GRIP_PX = 66;   // grip distance from centre, in screen pixels

const EMPTY: GeoJSON.FeatureCollection = { type: 'FeatureCollection', features: [] };

let installed = false;
let active: {
  center: [number, number];
  bearing: number;
  onCommit: (bearing: number) => void;
} | null = null;

/** Geodesic destination point `km` away from [lon,lat] along `bearingDeg`. */
function destPoint(lon: number, lat: number, km: number,
                   bearingDeg: number): [number, number] {
  const R = 6371.0088;
  const d = km / R;
  const brg = (bearingDeg * Math.PI) / 180;
  const lat1 = (lat * Math.PI) / 180;
  const lon1 = (lon * Math.PI) / 180;
  const lat2 = Math.asin(Math.sin(lat1) * Math.cos(d)
    + Math.cos(lat1) * Math.sin(d) * Math.cos(brg));
  const lon2 = lon1 + Math.atan2(
    Math.sin(brg) * Math.sin(d) * Math.cos(lat1),
    Math.cos(d) - Math.sin(lat1) * Math.sin(lat2));
  return [(lon2 * 180) / Math.PI, (lat2 * 180) / Math.PI];
}

/** Ground distance (km) that projects to GRIP_PX on screen at the centre. */
function gripDistanceKm(map: maplibregl.Map, center: [number, number]): number {
  const p = map.project({ lng: center[0], lat: center[1] });
  const up = map.unproject([p.x, p.y - GRIP_PX]);
  return Math.max(0.05, haversineKm(center[1], center[0], up.lat, up.lng));
}

function draw(map: maplibregl.Map): void {
  const source = map.getSource(SRC) as maplibregl.GeoJSONSource | undefined;
  if (!source) return;
  if (!active) { source.setData(EMPTY); return; }
  const { center, bearing } = active;
  const grip = destPoint(center[0], center[1], gripDistanceKm(map, center), bearing);
  source.setData({
    type: 'FeatureCollection',
    features: [
      { type: 'Feature', properties: {},
        geometry: { type: 'LineString', coordinates: [center, grip] } },
      { type: 'Feature', properties: {},
        geometry: { type: 'Point', coordinates: grip } },
    ],
  });
}

export function installRotateHandle(map: maplibregl.Map): void {
  if (installed) return;
  installed = true;
  map.addSource(SRC, { type: 'geojson', data: EMPTY });
  map.addLayer({
    id: ARM_LAYER, type: 'line', source: SRC,
    filter: ['==', ['geometry-type'], 'LineString'],
    paint: { 'line-color': '#ffd166', 'line-width': 1.6,
             'line-dasharray': [2, 2], 'line-opacity': 0.9 },
  });
  map.addLayer({
    id: GRIP_LAYER, type: 'circle', source: SRC,
    filter: ['==', ['geometry-type'], 'Point'],
    paint: { 'circle-radius': 7, 'circle-color': '#ffd166',
             'circle-stroke-color': '#12202c', 'circle-stroke-width': 2 },
  });

  let dragging = false;
  // Shared hover pass (hover.ts). Inert while dragging, so the 'grabbing' hand
  // set below is neither replaced nor cleared mid-drag.
  registerHover(map, { layers: [GRIP_LAYER], cursor: 'grab', when: () => !dragging });

  // Pointer events rather than MapLibre's layer-scoped `mousedown`: those never
  // fire for touch, so the grip could not be dragged with a finger at all. The
  // layer hit-test that the scoped listener gave us for free is done here with
  // queryRenderedFeatures, widened to a small box so a fingertip can catch a
  // 7px circle.
  const canvas = map.getCanvas();
  const pointAt = (ev: PointerEvent): [number, number] => {
    const rect = canvas.getBoundingClientRect();
    return [ev.clientX - rect.left, ev.clientY - rect.top];
  };
  canvas.addEventListener('pointerdown', (ev: PointerEvent) => {
    if (isMapEditing() || !active || dragging || !map.getLayer(GRIP_LAYER)) return;
    const [x, y] = pointAt(ev);
    const pad = ev.pointerType === 'mouse' ? 4 : 14;
    const hit = map.queryRenderedFeatures(
      [[x - pad, y - pad], [x + pad, y + pad]], { layers: [GRIP_LAYER] });
    if (!hit.length) return;
    ev.preventDefault();          // suppress map pan/rotate while dragging
    ev.stopPropagation();
    dragging = true;
    canvas.setPointerCapture(ev.pointerId);
    map.dragPan.disable();
    canvas.style.cursor = 'grabbing';
    const onMove = (move: PointerEvent): void => {
      if (!active || move.pointerId !== ev.pointerId) return;
      const at = map.unproject(pointAt(move));
      active.bearing = normalizeBearing(bearingDegBetween(
        active.center[1], active.center[0], at.lat, at.lng));
      draw(map);
    };
    const onUp = (up: PointerEvent): void => {
      if (up.pointerId !== ev.pointerId) return;
      canvas.removeEventListener('pointermove', onMove);
      canvas.removeEventListener('pointerup', onUp);
      canvas.removeEventListener('pointercancel', onUp);
      dragging = false;
      map.dragPan.enable();
      canvas.style.cursor = '';
      active?.onCommit(active.bearing);
    };
    canvas.addEventListener('pointermove', onMove);
    canvas.addEventListener('pointerup', onUp);
    canvas.addEventListener('pointercancel', onUp);
  });
  // Keep the grip glued to its screen offset as the map pans/zooms/rotates.
  map.on('move', () => { if (active) draw(map); });
}

export function showRotateHandle(
  map: maplibregl.Map,
  center: [number, number],
  bearing: number,
  onCommit: (bearing: number) => void,
): void {
  installRotateHandle(map);
  active = { center, bearing: normalizeBearing(bearing), onCommit };
  draw(map);
}

export function hideRotateHandle(map: maplibregl.Map): void {
  active = null;
  (map.getSource(SRC) as maplibregl.GeoJSONSource | undefined)?.setData(EMPTY);
}
